/**
 * AuthorIdentityEditor — editor for canonical Author identity fields.
 *
 * Renders author-level fields from openapi-content-plan.yaml > Author:
 *   • displayName              (TextField)
 *   • avatarUrl                (TextField)
 *   • positioning              (TextField multiline — 40-300 chars)
 *   • expertise                (TextField multiline — 80-500 chars)
 *   • originStory              (TextField multiline — 100-800 chars)
 *   • values                   (string[] — multiline newline-split)
 *   • uniqueValueProposition   (TextField multiline — 40-250 chars)
 *   • defaultLocale            (TextField — ISO locale code)
 *
 * Includes "Re-analyze identity" button that opens ConfirmActionDialog with
 * Available model selector → fires authorAnalyze({scope:'identity', model}).
 *
 * Save uses authorFirestoreService.updateAuthor() — patch-merge update with
 * stripUndefined.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { SAccordion } from '@modules/ui-components';
import {
  getAuthorFirestoreService,
  type AuthorDoc,
} from '../../services/AuthorFirestoreService';
import type { AuthorBasePath } from '../../services/AuthorPlatformService';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { ConfirmActionDialog, type ConfirmActionResult } from './ConfirmActionDialog';
import { useCollapsibleSection } from '../../hooks/useCollapsibleSection';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';

const TESTID = 'author-identity-editor';

const linesToText = (lines?: string[]): string => (lines ?? []).join('\n');
const textToLines = (text: string): string[] =>
  text.split('\n').map((s) => s.trim()).filter(Boolean);

export interface AuthorIdentityEditorProps {
  projectId: string;
  userId: string;
  author: AuthorDoc;
  /**
   * Discriminates between self-author (default `'authors'`) and tracked
   * creator (`'external_authors'`). Drives both the Firestore write target
   * and the `authorKind` flag passed to the re-analyze backend task.
   */
  basePath?: AuthorBasePath;
  /** Notified when re-analyze task starts so a parent task tray can subscribe. */
  onTaskStarted?: (taskId: string, label: string) => void;
}

interface DraftState {
  displayName: string;
  avatarUrl: string;
  positioning: string;
  expertise: string;
  originStory: string;
  values: string[];
  uniqueValueProposition: string;
  defaultLocale: string;
}

function authorToDraft(a: AuthorDoc): DraftState {
  return {
    displayName: a.displayName ?? '',
    avatarUrl: a.avatarUrl ?? '',
    positioning: a.positioning ?? '',
    expertise: a.expertise ?? '',
    originStory: a.originStory ?? '',
    values: [...(a.values ?? [])],
    uniqueValueProposition: a.uniqueValueProposition ?? '',
    defaultLocale: a.defaultLocale ?? '',
  };
}

export const AuthorIdentityEditor: React.FC<AuthorIdentityEditorProps> = ({
  projectId,
  userId,
  author,
  basePath = 'authors',
  onTaskStarted,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const authorService = getAuthorFirestoreService(basePath);
  const [draft, setDraft] = useState<DraftState>(authorToDraft(author));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [reanalyzeOpen, setReanalyzeOpen] = useState(false);
  const { expanded, setExpanded } = useCollapsibleSection('author-identity', true);

  useEffect(() => {
    setDraft(authorToDraft(author));
  }, [author]);

  const handleSave = async () => {
    if (!author.id) return;
    setSaving(true);
    setError(null);
    try {
      await authorService.updateAuthor(projectId, author.id, {
        displayName: draft.displayName || undefined,
        avatarUrl: draft.avatarUrl || undefined,
        positioning: draft.positioning || undefined,
        expertise: draft.expertise || undefined,
        originStory: draft.originStory || undefined,
        values: draft.values.length > 0 ? draft.values : undefined,
        uniqueValueProposition: draft.uniqueValueProposition || undefined,
        defaultLocale: draft.defaultLocale || undefined,
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save author identity');
    } finally {
      setSaving(false);
    }
  };

  const handleReanalyzeConfirm = async (result: ConfirmActionResult) => {
    if (!author.id) return;
    const client = new ContentPlanApiClient();
    const res = await client.authorAnalyze({
      projectId,
      userId,
      existingAuthorId: author.id,
      // scope='identity' — only re-derive author-level positioning/expertise/etc.
      // Voice/posts/pillars untouched. Schema 1.3.1+ extends AuthorOnboardingInput.
      // Schema 1.4.0+: authorKind selects external_authors collection on backend.
      ...({
        scope: 'identity',
        model: result.model,
        analysisInstructions: result.analysisInstructions,
        ...(basePath === 'external_authors'
          ? { authorKind: 'tracked-creator' }
          : {}),
      } as any),
    });
    if (!res.success || !res.taskId) {
      throw new Error(res.error ?? 'Re-analyze identity dispatch failed');
    }
    onTaskStarted?.(res.taskId, 'Re-analyze author identity');
  };

  return (
    <SAccordion
      title="Author identity"
      subtitle="Positioning, expertise, origin story, values, UVP — applied across all platforms. Edit manually or re-analyze with AI."
      collapsible
      expanded={expanded}
      onChange={setExpanded}
      data-testid={TESTID}
    >
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        {savedFlash && <Chip size="small" color="success" label="Saved" />}
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => setReanalyzeOpen(true)}
          data-testid={`${TESTID}-reanalyzeBtn`}
        >
          Re-analyze identity
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <TextField
            label="Display name"
            size="small"
            fullWidth
            value={draft.displayName}
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
            inputProps={{ 'data-testid': `${TESTID}-displayName` }}
          />
          <TextField
            label="Avatar URL"
            size="small"
            fullWidth
            value={draft.avatarUrl}
            onChange={(e) => setDraft({ ...draft, avatarUrl: e.target.value })}
            inputProps={{ 'data-testid': `${TESTID}-avatarUrl` }}
          />
          <TextField
            label="Locale"
            size="small"
            sx={{ width: 120 }}
            value={draft.defaultLocale}
            onChange={(e) => setDraft({ ...draft, defaultLocale: e.target.value })}
            placeholder="en-US"
            inputProps={{ 'data-testid': `${TESTID}-defaultLocale` }}
          />
        </Stack>

        <TextField
          label="Positioning"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={draft.positioning}
          onChange={(e) => setDraft({ ...draft, positioning: e.target.value })}
          helperText="One sentence — who I am, for whom, what's unique. 40-300 chars recommended."
          inputProps={{ 'data-testid': `${TESTID}-positioning` }}
        />

        <TextField
          label="Expertise"
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={draft.expertise}
          onChange={(e) => setDraft({ ...draft, expertise: e.target.value })}
          helperText="Why you have the right to speak — experience, achievements, credentials. 80-500 chars."
          inputProps={{ 'data-testid': `${TESTID}-expertise` }}
        />

        <TextField
          label="Origin story"
          size="small"
          fullWidth
          multiline
          minRows={4}
          value={draft.originStory}
          onChange={(e) => setDraft({ ...draft, originStory: e.target.value })}
          helperText="Why you started writing about this; transformation undergone. 100-800 chars."
          inputProps={{ 'data-testid': `${TESTID}-originStory` }}
        />

        <TextField
          label="Values"
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={linesToText(draft.values)}
          onChange={(e) => setDraft({ ...draft, values: textToLines(e.target.value) })}
          helperText="3-5 principles defining your point of view. One per line."
          inputProps={{ 'data-testid': `${TESTID}-values` }}
        />

        <TextField
          label="Unique value proposition"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={draft.uniqueValueProposition}
          onChange={(e) => setDraft({ ...draft, uniqueValueProposition: e.target.value })}
          helperText="How your perspective differs from others on the same topics. 40-250 chars."
          inputProps={{ 'data-testid': `${TESTID}-uvp` }}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            disabled={saving}
            onClick={handleSave}
            data-testid={`${TESTID}-saveBtn`}
          >
            {saving ? 'Saving…' : 'Save identity'}
          </Button>
        </Stack>
      </Stack>

      <ConfirmActionDialog
        open={reanalyzeOpen}
        projectId={projectId}
        title="Re-analyze author identity"
        description="AI will re-derive positioning, expertise, origin story, values, and UVP from the existing scraped corpus across all platforms. Voice, post examples, and posts will NOT be touched."
        mode="ai"
        defaultModel={projectDefaultModel.value}
        confirmLabel="Re-analyze"
        onConfirm={handleReanalyzeConfirm}
        onClose={() => setReanalyzeOpen(false)}
      />
    </SAccordion>
  );
};

export default AuthorIdentityEditor;
